import React from "react";
import { Search, GitBranch } from "lucide-react";
import { ToolCardShell, type CardStatus } from "../tool";
import { ToolInput, ToolOutput } from "../tool";
import type { ToolCardComponentProps } from "../tool-registry";

const TOOL_NAME_LABELS: Record<string, string> = {
  search: "搜索",
  neo4j: "知识图谱查询",
};

const QUERY_TYPE_LABELS: Record<string, string> = {
  entity: "实体查询",
  relationship: "关系查询",
  path: "路径查询",
};

const SearchCardContent: React.FC<ToolCardComponentProps> = ({ toolCall }) => {
  const cardStatus: CardStatus = toolCall.status || "detected";
  const args = toolCall.args || {};
  const result = toolCall.result;
  const toolName = toolCall.toolName;
  const isNeo4j = toolName === "neo4j";

  const query = String(args.query || args.query_text || "");
  const sources = args.sources as string[] | undefined;

  let hitCount = 0;
  if (result) {
    if (typeof result === "object" && result !== null) {
      const r = result as Record<string, unknown>;
      if (Array.isArray(r.results)) hitCount = r.results.length;
      else if (Array.isArray(r.items)) hitCount = r.items.length;
      else if (typeof r.count === "number") hitCount = r.count;
      else if (typeof r.total === "number") hitCount = r.total;
    }
  }

  const handleKnowledgeGraphClick = () => {
    const event = new CustomEvent("switchToKnowledgeGraph");
    window.dispatchEvent(event);
  };

  const renderSummary = () => {
    if (isNeo4j) {
      const queryType = String(args.query_type || "entity");
      const entity = String(args.entity || "");
      const depth = args.max_depth as number | undefined;
      const limit = args.limit as number | undefined;

      return (
        <div className="px-4 py-2 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
          <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-purple-700">
            {QUERY_TYPE_LABELS[queryType] || queryType}
          </span>
          {entity && (
            <span className="truncate max-w-[200px]">目标: {entity}</span>
          )}
          {depth != null && <span>深度: {depth}</span>}
          {limit != null && <span>限制: {limit}</span>}
          {cardStatus === "success" && hitCount > 0 && (
            <span className="text-success">命中 {hitCount} 条</span>
          )}
        </div>
      );
    }

    return (
      <div className="px-4 py-2 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
        {query && (
          <span className="truncate max-w-[200px]">
            关键词: <span className="text-foreground font-medium">{query}</span>
          </span>
        )}
        {sources && sources.length > 0 && (
          <>
            {sources.map((src) => (
              <span
                key={src}
                className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-blue-700"
              >
                {src}
              </span>
            ))}
          </>
        )}
        {cardStatus === "success" && hitCount > 0 && (
          <span className="text-success">命中 {hitCount} 条</span>
        )}
      </div>
    );
  };

  const icon = isNeo4j ? (
    <GitBranch className="h-4 w-4" />
  ) : (
    <Search className="h-4 w-4" />
  );
  const iconColor = isNeo4j ? "text-purple-500" : "text-blue-500";

  return (
    <ToolCardShell
      toolName={TOOL_NAME_LABELS[toolName] || toolName}
      status={cardStatus}
      icon={icon}
      iconColor={iconColor}
      defaultExpanded={false}
    >
      {renderSummary()}
      {toolCall.args && Object.keys(toolCall.args).length > 0 && (
        <ToolInput data={toolCall.args} />
      )}
      {(toolCall.result !== undefined && toolCall.result !== null) || toolCall.error ? (
        <>
          <ToolOutput data={toolCall.result} error={toolCall.error} />
          {isNeo4j &&
            cardStatus === "success" &&
            !toolCall.error && (
              <div className="px-4 pb-3">
                <button
                  onClick={handleKnowledgeGraphClick}
                  className="text-xs flex items-center gap-1 px-3 py-1.5 bg-purple-500/10 hover:bg-purple-500/20 text-purple-600 rounded-md transition-colors border border-purple-500/20"
                  type="button"
                >
                  查看知识图谱
                </button>
              </div>
            )}
        </>
      ) : null}
    </ToolCardShell>
  );
};

export { SearchCardContent as SearchCard };
